import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react' // build:20260620
import {
  DiceFive as Dice,
  Robot,
  Question,
  SunDim,
  Moon,
  House,
  SignOut,
  Signpost,
  UserCircle,
  Fire,
  ShieldCheck,
  Scales,
  Lightning,
  Article,
  BookOpenText,
} from '@phosphor-icons/react'
import { useTheme } from '../hooks/useTheme'
import { useAuth } from '../auth'
import { cn } from '../lib/utils'
import { ToastHost } from './Toast'
import InstallPrompt from './InstallPrompt'
import FeedbackButton from './FeedbackButton'
import CookieConsent from './CookieConsent'
import UpdatePrompt from './UpdatePrompt'
import OfflineBanner from './OfflineBanner'
import { resetConsent } from '../lib/analytics'
import { hasPremiumAccess } from '../api'

// All seven labels don't fit the max-w-7xl header (they wrapped onto 2–3
// lines), so only the core product items show a label (xl+); the rest are
// icon-only with a tooltip and aria-label.
const APP_NAV = [
  { to: '/app', label: 'Decisions', icon: House, testId: 'nav-decisions', core: true },
  { to: '/compare', label: 'Compare', icon: Scales, testId: 'nav-compare', core: true },
  { to: '/agent', label: 'AI Decision Coach', icon: Robot, testId: 'nav-agent', core: true },
  { to: '/explore', label: 'Explore', icon: Fire, testId: 'nav-explore', core: true },
  { to: '/how-to', label: 'How To', icon: BookOpenText, testId: 'nav-how-to', core: false },
  { to: '/how-it-works', label: 'How It Works', icon: Question, testId: 'nav-how', core: false },
  { to: '/should-i-quit', label: 'Should I Quit?', icon: Signpost, testId: 'nav-siq', core: false },
  { to: '/blog', label: 'Blog', icon: Article, testId: 'nav-blog', core: false },
]

const PUBLIC_NAV = [
  { to: '/#features', label: 'Features' },
  { to: '/explore', label: 'Explore' },
  { to: '/how-to', label: 'How to' },
  { to: '/how-it-works', label: 'How it works' },
  { to: '/blog', label: 'Blog' },
  { to: '/pricing', label: 'Pricing' },
]

export default function Layout({ children }: { children: React.ReactNode }) {
  const { theme, toggle } = useTheme()
  const auth = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const isLanding = location.pathname === '/'
  const isAuth = location.pathname === '/login' || location.pathname === '/signup'
  // 'Should I Quit' and 'Where Can I Retire' are standalone funnels — most
  // visitors arrive cold from search or a vanity domain with one question, not
  // looking for an app. Showing the seven-item product nav before they've got
  // an answer reads as "here's a whole app to learn" and pushes them out.
  // Both tools need no account, so the brand mark carries provenance and each
  // page's own post-result CTA does the converting — after the value has
  // landed, not before it.
  const STANDALONE_PREFIXES = ['/should-i-quit', '/where-can-i-retire']
  const isStandalone = STANDALONE_PREFIXES.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/'),
  )
  const showAppChrome = !isLanding && !isAuth && !isStandalone
  const [menuOpen, setMenuOpen] = useState(false)
  // Keep the active nav item visible when the icon row scrolls (phones).
  // scrollLeft, not scrollIntoView, so the page itself never jumps.
  const navScrollRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const row = navScrollRef.current
    const active = row?.querySelector<HTMLElement>('[aria-current="page"]')
    if (!row || !active) return
    const left = active.offsetLeft - row.offsetLeft
    if (left < row.scrollLeft || left + active.offsetWidth > row.scrollLeft + row.clientWidth) {
      row.scrollLeft = left - (row.clientWidth - active.offsetWidth) / 2
    }
  }, [location.pathname])

  // On client-side navigation, move focus to the new page's content so screen
  // readers announce it and keyboard users don't restart from the old spot.
  const firstRender = useRef(true)
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    document.getElementById('main')?.focus({ preventScroll: true })
  }, [location.pathname])

  return (
    <div className="min-h-screen flex flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:px-4 focus:py-2 focus:rounded-xl focus:bg-surface focus:text-ink focus:font-bold focus:border-2 focus:border-edge"
      >
        Skip to content
      </a>
      <header className="sticky top-0 z-40 backdrop-blur-md bg-bg/80 border-b-2 border-edge">
        <OfflineBanner />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="logo-home-link" aria-label="LifeOdds home">
            <div className="relative w-11 h-11 rounded-xl bg-primary border-2 border-edge shadow-brutal-sm grid place-items-center transition-transform group-hover:rotate-[-6deg] group-hover:-translate-y-0.5">
              <Dice size={24} weight="fill" className="text-white" />
            </div>
            <div className="hidden sm:block">
              <div className="font-heading font-black text-xl leading-none tracking-tight">
                LifeOdds
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-muted mt-0.5">
                Know before you go.
              </div>
            </div>
          </Link>

          <nav className="flex items-center gap-1 sm:gap-2 min-w-0">
            {isLanding && !isAuth && (
              <>
                {PUBLIC_NAV.map((n) => (
                  <a key={n.to} href={n.to} className="hidden sm:inline px-3 py-2 rounded-xl font-bold text-sm hover:bg-muted" data-testid={`public-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    {n.label}
                  </a>
                ))}
                <Link to="/app" className="hidden sm:inline px-3 py-2 rounded-xl font-bold text-sm hover:bg-muted" data-testid="nav-open-app">
                  Dashboard
                </Link>
              </>
            )}

            {/* On a phone the icon row is wider than the viewport, so it scrolls
                sideways on its own instead of widening the whole page. The user
                menu and theme toggle stay outside the scroller so the dropdown
                isn't clipped; py/pr keep the brutal shadows visible. */}
            {showAppChrome && (
              <div ref={navScrollRef} className="flex items-center gap-1 sm:gap-2 min-w-0 overflow-x-auto scrollbar-none py-1 pr-1">
              {APP_NAV.map((n) => {
                const Icon = n.icon
                const active = n.to === '/app'
                  ? location.pathname === '/app' || location.pathname.startsWith('/decision') || location.pathname.startsWith('/results')
                  : location.pathname === n.to || location.pathname.startsWith(n.to + '/')
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={n.testId}
                    aria-label={n.label}
                    title={n.label}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'shrink-0 whitespace-nowrap flex items-center gap-1.5 px-3 py-2 rounded-xl font-bold text-sm border-2 transition-all',
                      active
                        ? 'bg-primary text-white border-edge shadow-brutal-sm'
                        : 'border-transparent hover:border-edge hover:bg-muted',
                    )}
                  >
                    <Icon size={18} weight={active ? 'fill' : 'regular'} />
                    {n.core && <span className="hidden xl:inline">{n.label}</span>}
                  </Link>
                )
              })}


              {/* Admin link — only visible to admins */}
              {auth.user?.role === 'admin' && (
                <Link
                  to="/admin"
                  aria-label="Admin"
                  title="Admin"
                  className={cn(
                    'shrink-0 whitespace-nowrap flex items-center gap-1.5 px-3 py-2 rounded-xl font-bold text-sm border-2 transition-all',
                    location.pathname.startsWith('/admin')
                      ? 'bg-ink text-white border-edge shadow-brutal-sm'
                      : 'border-transparent hover:border-edge hover:bg-muted',
                  )}
                >
                  <ShieldCheck size={18} weight={location.pathname.startsWith('/admin') ? 'fill' : 'regular'} />
                  <span className="hidden xl:inline">Admin</span>
                </Link>
              )}
              </div>
            )}

            {/* Upgrade link — only for free logged-in users. Outside the scroller
                so it never scrolls out of view. */}
            {showAppChrome && auth.user && !hasPremiumAccess(auth.user) && (
              <Link
                to="/pricing"
                data-testid="nav-upgrade-btn"
                aria-label="Upgrade"
                title="Upgrade"
                className="shrink-0 whitespace-nowrap flex items-center gap-1.5 px-3 py-2 rounded-xl font-bold text-sm text-primary-ink border-2 border-primary/40 hover:bg-primary/10 transition-all"
              >
                <Lightning size={16} weight="fill" />
                <span className="hidden xl:inline">Upgrade</span>
              </Link>
            )}

            {/* Auth user controls */}
            {auth.user === undefined ? (
              <div className="w-6 h-6 rounded-full border-2 border-edge animate-pulse bg-muted" />
            ) : auth.user ? (
              <div className="relative">
                <button
                  onClick={() => setMenuOpen((v) => !v)}
                  data-testid="user-menu-btn"
                  className="flex items-center gap-1 ml-1 px-2 py-1.5 rounded-xl border-2 border-edge bg-surface shadow-brutal-sm hover:-translate-y-0.5 hover:shadow-brutal transition-all"
                >
                  <UserCircle size={18} weight="duotone" />
                  <span className="hidden sm:inline font-bold text-sm truncate max-w-[120px]">
                    {auth.user.name || auth.user.email}
                  </span>
                </button>
                {menuOpen && (
                  <>
                    <button className="fixed inset-0 z-30 cursor-default" onClick={() => setMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 z-40 w-48 card-brutal p-2" data-testid="user-menu">
                      <div className="px-3 py-2 text-xs text-ink-muted truncate">{auth.user.email}</div>
                      <Link to="/app" onClick={() => setMenuOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-muted text-sm font-bold" data-testid="menu-open-app">
                        Dashboard
                      </Link>
                      <Link to="/settings/account" onClick={() => setMenuOpen(false)} className="block px-3 py-2 rounded-lg hover:bg-muted text-sm font-bold" data-testid="menu-account-settings">
                        Account &amp; preferences
                      </Link>
                      <button
                        onClick={async () => { await auth.logout(); setMenuOpen(false); navigate('/') }}
                        data-testid="logout-btn"
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-primary/10 text-sm font-bold text-primary-ink inline-flex items-center gap-1"
                      >
                        <SignOut size={14} weight="bold" /> Log out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              // Signed-in users keep their menu (it's their way back into the
              // app), but a cold visitor to the standalone tool is not asked to
              // sign up before they've seen an answer — the tool needs no
              // account, and the post-result CTA converts once value has landed.
              !isAuth && !isStandalone && (
                <div className="flex items-center gap-1 ml-1">
                  <Link to="/login" data-testid="nav-login-btn" className="hidden sm:inline px-3 py-2 rounded-xl font-bold text-sm hover:bg-muted">
                    Sign in
                  </Link>
                  <Link to="/signup" data-testid="nav-signup-btn" className="px-3 py-2 rounded-xl bg-primary text-white font-bold text-sm border-2 border-edge shadow-brutal-sm hover:-translate-y-0.5 hover:shadow-brutal transition-all">
                    Sign up
                  </Link>
                </div>
              )
            )}

            <button
              onClick={toggle}
              data-testid="theme-toggle-btn"
              className="ml-1 p-2 rounded-xl border-2 border-edge bg-surface shadow-brutal-sm hover:-translate-y-0.5 hover:shadow-brutal transition-all"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <SunDim size={18} weight="fill" /> : <Moon size={18} weight="fill" />}
            </button>
          </nav>
        </div>
      </header>

      <main id="main" tabIndex={-1} className={cn('outline-none flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8', isLanding ? 'py-6 sm:py-8' : 'py-8 sm:py-12')}>
        {children}
      </main>

      <footer className="mt-16 border-t-2 border-edge bg-surface/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-sm text-ink-muted flex flex-wrap items-center justify-between gap-4">
          {isStandalone ? (
            // Standalone tool: attribution instead of product navigation. This
            // is the quiet nudge that replaces the nav — it tells a cold
            // visitor where the tool came from without asking them to go
            // anywhere before they have their answer. Legal links stay (ECG
            // §5 / GDPR); 'Should I Quit?' is dropped because it linked here.
            <span>
              A free tool by{' '}
              <Link to="/" className="font-bold text-ink hover:text-primary-ink transition-colors" data-testid="siq-footer-brand">
                LifeOdds
              </Link>
              {' '}— we model big life decisions with real probabilities.
            </span>
          ) : (
            <span className="font-bold">LifeOdds</span>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {!isStandalone && (
              <>
                <Link to="/how-to" className="hover:text-ink transition-colors">How to</Link>
                <Link to="/decisions" className="hover:text-ink transition-colors">Decision guides</Link>
                <Link to="/should-i-quit" className="hover:text-ink transition-colors">Should I Quit?</Link>
                <Link to="/pricing" className="hover:text-ink transition-colors">Pricing</Link>
                <Link to="/about" className="hover:text-ink transition-colors">About</Link>
                <Link to="/support" className="hover:text-ink transition-colors">Support</Link>
              </>
            )}
            <Link to="/methodology" className="hover:text-ink transition-colors">Methodology</Link>
            <Link to="/privacy" className="hover:text-ink transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-ink transition-colors">Terms</Link>
            <Link to="/imprint" className="hover:text-ink transition-colors">Imprint</Link>
            <button onClick={resetConsent} className="hover:text-ink transition-colors" data-testid="cookie-settings-btn">
              Cookie settings
            </button>
          </div>
          {!isStandalone && <span className="mono">Odds · Assumptions · Flip-points</span>}
        </div>
      </footer>
      <ToastHost />
      <FeedbackButton />
      <InstallPrompt />
      <CookieConsent />
      <UpdatePrompt />
    </div>
  )
}
